import React, { Fragment, useCallback, useEffect, useRef } from 'react';
import { useHistory } from "react-router-dom";
import ProspectTabBackButton from "v4/components/utils/ProspectTabBackButton/ProspectTabBackButton";
import ProspectTabEditButton from "v4/components/utils/ProspectTabEditButton/ProspectTabEditButton";
import { useProspectPageContext } from "v4/contexts/ProspectPageContext";
import { QUOTE_ITEM_FORM } from "v4/data/apiRoutes";
import ActionsButtonsPdf from "v4/features/front/generatePdf/components/ActionsButtonsPdf/ActionsButtonsPdf";
import PennylaneButton from "v4/features/front/pennylane/components/PennylaneButton/PennylaneButton";
import ShowEntityLayout from "v4/layouts/ShowEntityLayout/ShowEntityLayout";
import ToggleComponent from "v4/components/utils/ToggleComponent/ToggleComponent";
import ProspectQuotesReoffer
    from "v4/pages/front/Prospect/components/ProspectTabsSwitcher/components/partials/ProspectQuotesReoffer/ProspectQuotesReoffer";
import ProspectQuotesSplitButton
    from "v4/pages/front/Prospect/components/ProspectTabsSwitcher/components/partials/ProspectQuotesSplitButton/ProspectQuotesSplitButton";
import ProspectQuotesTabs
    from "v4/pages/front/Prospect/components/ProspectTabsSwitcher/components/partials/ProspectQuotesTabs/ProspectQuotesTabs";
import ProspectTabShowActions
    from "v4/pages/front/Prospect/components/ProspectTabsSwitcher/components/partials/ProspectTabShowActions/ProspectTabShowActions";
import QuoteTasksButton
    from "v4/pages/front/Prospect/components/ProspectTabsSwitcher/components/ProspectQuotesTab/components/ProspectQuoteTabShow/components/QuoteTasksButton/QuoteTasksButton";
import { useQuoteTabContext } from "v4/pages/front/Prospect/components/ProspectTabsSwitcher/contexts/QuoteTabContext.jsx";
import QuoteVoter from "v4/voters/quoteVoter";
import { filterFilesForMail } from "v4/utils";

export default function BaseQuoteTabShow({
                                             prospectId,
                                             showId,
                                             onGetData,
                                             quoteData,
                                             setQuoteData,
                                             fetchFormParameters = {},
                                             specificButtonsByOrder = []
                                         }) {
    const { replace } = useHistory();
    const { quote, setQuote } = useProspectPageContext();
    const { setReofferConfig, reofferOpenId, closeReoffer } = useQuoteTabContext();
    const { canReofferQuote } = QuoteVoter();

    const pdfOpenerRef = useRef(null);
    const registerPdfOpener = useCallback((opener) => { pdfOpenerRef.current = opener; }, []);

    const onReoffered = useCallback(({ withPdf }) => {
        replace({ state: { showHasToRefresh: true, dontRefreshProspectView: true } });

        const openPdfModal = pdfOpenerRef.current;
        if (withPdf && openPdfModal) openPdfModal();
    }, [replace]);

    const currentState = quote?.metadata?.currentState;
    const isReofferVisible = canReofferQuote(currentState);
    useEffect(() => {
        setReofferConfig({ quoteId: showId, isVisible: isReofferVisible });

        return () => setReofferConfig(null);
    }, [showId, isReofferVisible, setReofferConfig]);

    useEffect(() => {
        if (quoteData) {
            const quoteFromHydra = Object.values(quoteData.results).reduce((acc, { properties }) => {
                properties.forEach(({ key, value }) => {
                    acc[key] = value;
                })
                return acc;
            }, {});
            quoteFromHydra.metadata = quoteData.metadata;
            setQuote(quoteFromHydra);
        }
    }, [quoteData, setQuote]);

    useEffect(() => {
        return () => setQuote(null);
    }, [setQuote]);

    const whenDeleteFile = () => {
        replace({ state: { hasToRefresh: true } })
    }

    const buttonsByOrder = [
        {
            order: 1,
            component: <ProspectTabBackButton/>
        },
        {
            order: 2,
            component: <ProspectTabEditButton showId={showId}/>
        },
        {
            order: 3,
            component: (!quote?.metadata?.isSplit && quote?.metadata?.canSplit) && <ProspectQuotesSplitButton/>
        },
        {
            order: 4,
            component: <QuoteTasksButton quote={quote}/>
        },
        {
            order: 7,
            component: <ActionsButtonsPdf prospectId={prospectId}
                                          quoteId={showId}
                                          quoteName={quote?.name}
                                          contactIds={quote?.contactId}
                                          files={quote?.metadata?.files}
                                          currentFile={filterFilesForMail(quote?.customerFiles)}
                                          registerOpenModal={registerPdfOpener}
                                          updateHandler={onPDFGenerate(setQuoteData)}/>
        },
        {
            order: 8,
            component: <PennylaneButton quote={quote}/>
        },
        {
            order: 9,
            component: quote?.metadata?.showActions && (
                <ProspectTabShowActions showId={showId}
                                        prospectId={prospectId}
                                        popupTitle={quote?.name}
                                        showActions={quote.metadata.showActions}/>
            )
        },
        ...specificButtonsByOrder,
    ]

    return (
        <>
            <div className="prospect-tab__prospect__header">
                {quote?.metadata?.isSplit && <ProspectQuotesTabs/>}

                {buttonsByOrder.sort((a, b) => a.order - b.order).map(({ order, component }, index) => (
                    <Fragment key={index}>
                        {component}
                    </Fragment>
                ))}
            </div>
            <ToggleComponent isShowing={reofferOpenId} shouldRemoveFromDom className="reoffer-popup">
                <ProspectQuotesReoffer rowIdToReoffer={reofferOpenId}
                                       isReofferPopupOpen={!!reofferOpenId}
                                       closePopup={closeReoffer}
                                       onReoffered={onReoffered}/>
            </ToggleComponent>
            <ShowEntityLayout entityId={showId}
                              onGetData={onGetData}
                              overrideData={quoteData}
                              whenDeleteFile={whenDeleteFile}
                              entityRouteName={QUOTE_ITEM_FORM}
                              fetchFormParameters={fetchFormParameters}/>
        </>
    )
}

/**
 * This function is used to update the quote data after a PDF generation or deletion.
 * It's exported here so it can be reused in other components if needed.
 *
 * @param {Function} setQuoteData
 * @returns {(file: Object, mode?: ('add'|'remove')) => void}
 */
export function onPDFGenerate(setQuoteData) {
    return (file, mode = 'add') => {
        if (mode === 'remove') {
            setQuoteData((quoteData) => {
                const _data = window.structuredClone(quoteData);
                if (Array.isArray(_data.metadata?.files)) {
                    _data.metadata.files = _data.metadata.files.filter(({ id }) => id !== file.id);
                }

                return _data;
            });

            return;
        }

        setQuoteData((quoteData) => {
            const _data = window.structuredClone(quoteData);
            const groupIdOfCustomerFiles = Object.keys(_data.results ?? {}).find((groupId) => _data.results[groupId].properties.some(({ key }) => key === 'customerFiles'));

            if (file.quotePdf) {
                const metadata = _data.metadata ?? {};

                metadata.files = Array.isArray(metadata.files) ? [...metadata.files, file] : [file];

                _data.metadata = metadata;
            }

            if (!groupIdOfCustomerFiles) return _data;
            const customerFiles = _data.results[groupIdOfCustomerFiles].properties.find(({ key }) => key === 'customerFiles');
            if (!Array.isArray(customerFiles.value)) customerFiles.value = [];

            // remove previous file from customerFiles
            if (file.previousFileId) {
                customerFiles.value = customerFiles.value.filter(({ id }) => id !== file.previousFileId);
            }

            // add new file to the top of the list
            customerFiles.value.unshift(file);

            return _data;
        })
    }
}
